import { useEffect, useState } from "react";
import { fetchCurrentUserOrThrow } from "../api";
import { COUNTRY_NAMES } from "../utils/countries";

/**
 * Resolves the signed-in user's home retail unit from their profile (JWT /
 * MS Graph `country` claim) so it can be pre-selected as the default Retail
 * Unit. Falls back gracefully when the claim is missing or unrecognised.
 */

const HOME_COUNTRY_KEY = "mcc-home-country";

// Module-level cache shared across hook instances/pages.
// undefined = not yet resolved this session, null = resolved to "none".
let cached: string | null | undefined;
let inFlight: Promise<string | null> | null = null;

function readStored(): string | null {
    try {
        return localStorage.getItem(HOME_COUNTRY_KEY);
    } catch {
        return null;
    }
}

function writeStored(code: string): void {
    try {
        localStorage.setItem(HOME_COUNTRY_KEY, code);
    } catch {
        /* ignore */
    }
}

/**
 * Map a Microsoft Graph `country` value to a known alpha-2 retail-unit code.
 * The claim can be either a code ("DE") or a country name ("Germany"), so we
 * accept both. Returns null if it can't be matched to a country we know.
 */
export function resolveCountryCode(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;

    // Already an alpha-2 code we recognise.
    const upper = trimmed.toUpperCase();
    if (COUNTRY_NAMES[upper]) return upper;

    // Otherwise try to match a country name (case-insensitive).
    const lower = trimmed.toLowerCase();
    for (const [code, name] of Object.entries(COUNTRY_NAMES)) {
        if (name.toLowerCase() === lower) return code;
    }
    return null;
}

export interface HomeCountryState {
    /**
     * Best-known home retail-unit code (e.g. "SE"): the resolved value once
     * settled, or the optimistic last-known stored value (or null) before then.
     */
    home: string | null;
    /**
     * Whether home-country resolution has settled this session — i.e. the
     * in-module session cache was already populated, or the `/auth/me` fetch
     * has completed (even if it failed and fell back to the stored value).
     *
     * Note: a value read synchronously from localStorage does NOT count as
     * resolved — it is shown optimistically via `home` while the fetch is still
     * pending. Callers use this to avoid acting on the provisional default
     * before the real home country is known (see the page self-heal guards).
     */
    resolved: boolean;
}

/**
 * Resolve the home country once per session, memoising the result.
 *
 * - Success → caches the resolved code (or null when the user genuinely has no
 *   recognised country) — a definitive answer that later mounts reuse.
 * - Transient failure (non-OK response / network error) → NOT cached: resets
 *   `inFlight` so a later mount retries, and falls back to the stored value.
 */
function resolveHomeCountry(): Promise<string | null> {
    if (cached !== undefined) return Promise.resolve(cached);
    if (!inFlight) {
        inFlight = fetchCurrentUserOrThrow()
            .then((user) => {
                cached = resolveCountryCode(user?.country ?? null);
                if (cached) writeStored(cached);
                return cached;
            })
            .catch(() => {
                inFlight = null;
                return readStored();
            });
    }
    return inFlight;
}

/**
 * Returns the user's home retail-unit code (e.g. "SE"), or null if unknown,
 * together with a `resolved` flag indicating whether resolution has settled.
 *
 * Reads the last-known value from localStorage synchronously so it is
 * available on the very first render of repeat visits, and refreshes it from
 * `/auth/me` in the background.
 */
export function useHomeCountry(): HomeCountryState {
    const [state, setState] = useState<HomeCountryState>(() =>
        cached !== undefined
            ? { home: cached, resolved: true }
            : { home: readStored(), resolved: false },
    );

    useEffect(() => {
        if (state.resolved) return; // settled synchronously from the cache
        let active = true;
        void resolveHomeCountry().then((code) => {
            // `resolved: true` means "resolution finished", not "value is
            // authoritative". `code` is already correct for every path: the
            // stored fallback on failure, the resolved code on success, or a
            // definitive null when the user genuinely has no country.
            if (active) setState({ home: code, resolved: true });
        });
        return () => {
            active = false;
        };
        // Runs at most once: `resolved` only transitions false→true and never
        // resets within a mount, so this never re-subscribes.
    }, [state.resolved]);

    return state;
}
